import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  CubeIcon,
  ArrowRightOnRectangleIcon,
  BeakerIcon,
  ArchiveBoxIcon,
  TruckIcon,
  DocumentTextIcon,
  FireIcon,
  BookOpenIcon,
  SparklesIcon,
  CalculatorIcon,
  GlobeAltIcon,
  UsersIcon,
  ShoppingBagIcon,
  Cog8ToothIcon,
  InboxIcon,
  TagIcon,
  ScaleIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';

const mainNavItems = [
  { label: 'Dashboard', icon: HomeIcon, path: '/company/dashboard' },
  { label: 'Customers', icon: UserGroupIcon, path: '/company/customers', permKey: 'access_customers' },
  { label: 'Products', icon: CubeIcon, path: '/company/products', permKey: 'access_products' },
  { label: 'Orders', icon: ClipboardDocumentListIcon, path: '/company/orders', permKey: 'access_orders' },
  { label: 'Settings', icon: Cog6ToothIcon, path: '/company/settings' },
  { label: 'Support', icon: LifebuoyIcon, path: '/company/support', permKey: 'access_support' },
];

// permKey: if set, staff need this permission to see the item; owners see all
const warehouseNavItems = [
  { label: 'Ingredients', icon: BeakerIcon, path: '/company/ingredients', permKey: 'access_ingredients' },
  { label: 'Suppliers', icon: TruckIcon, path: '/company/suppliers', permKey: 'access_suppliers' },
  { label: 'Inventory', icon: ArchiveBoxIcon, path: '/company/inventory', permKey: 'access_inventory' },
  { label: 'Purchase Orders', icon: DocumentTextIcon, path: '/company/purchase-orders', permKey: 'access_purchase_orders' },
];

const kitchenNavItems = [
  { label: 'Starters', icon: FireIcon, path: '/company/starters', permKey: 'access_starters' },
  { label: 'Recipes', icon: BookOpenIcon, path: '/company/recipes', permKey: 'access_recipes' },
  { label: 'Production', icon: SparklesIcon, path: '/company/production', permKey: 'access_production' },
];

const toolsNavItems = [
  { label: 'Batch Optimizer', icon: SparklesIcon, path: '/company/batch-optimizer', permKey: 'access_batch_optimizer' },
  { label: 'Cost Calculator', icon: CalculatorIcon, path: '/company/cost-calculator', permKey: 'access_cost_calculator' },
  { label: "Baker's Calculator", icon: ScaleIcon, path: '/company/bakers-calculator', permKey: 'access_baker_calculator' },
];

const webstoreNavItems = [
  { label: 'Store Settings', icon: Cog8ToothIcon, path: '/company/webstore/settings', permKey: 'access_store_settings' },
  { label: 'Store Customers', icon: UsersIcon, path: '/company/webstore/customers', permKey: 'access_store_customers' },
  { label: 'Store Orders', icon: ShoppingBagIcon, path: '/company/webstore/orders', permKey: 'access_store_orders' },
  { label: 'Coupon Codes', icon: TagIcon, path: '/company/webstore/coupons', permKey: 'access_store_settings' },
  { label: 'Messages', icon: InboxIcon, path: '/company/webstore/messages', permKey: 'access_messages' },
];

const CompanySidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const isOwner = !user?.is_company_admin || user?.is_company_owner;
  const perms: Record<string, boolean> = (user?.permissions as Record<string, boolean>) || {};

  // For staff (sub-admins), filter by permKey; owners see everything
  const canSee = (item: { permKey?: string }) =>
    isOwner || !item.permKey || perms[item.permKey] === true;

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
  };

  return (
    <nav
      style={{
        width: '220px',
        minWidth: '220px',
        height: '100vh',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
        backgroundColor: '#f0fdf4',
        borderRight: '1px solid #d1fae5',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 10px',
        zIndex: 100,
      }}
    >
      {mainNavItems.filter(canSee).map(({ label, icon: Icon, path }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '4px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive(path) ? '#065f46' : 'transparent',
            color: isActive(path) ? 'white' : '#374151',
            fontWeight: isActive(path) ? '600' : '400',
            fontSize: '0.9rem',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d1fae5';
            }
          }}
          onMouseLeave={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
          {label}
        </button>
      ))}

      {/* Warehouse section */}
      {warehouseNavItems.some(canSee) && (
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#9ca3af',
        padding: '14px 14px 6px',
      }}>
        Warehouse
      </div>
      )}
      {warehouseNavItems.filter(canSee).map(({ label, icon: Icon, path }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '4px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive(path) ? '#065f46' : 'transparent',
            color: isActive(path) ? 'white' : '#374151',
            fontWeight: isActive(path) ? '600' : '400',
            fontSize: '0.9rem',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d1fae5';
            }
          }}
          onMouseLeave={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
          {label}
        </button>
      ))}

      {/* Kitchen section */}
      {kitchenNavItems.some(canSee) && (
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#9ca3af',
        padding: '14px 14px 6px',
      }}>
        Kitchen
      </div>
      )}
      {kitchenNavItems.filter(canSee).map(({ label, icon: Icon, path }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '4px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive(path) ? '#065f46' : 'transparent',
            color: isActive(path) ? 'white' : '#374151',
            fontWeight: isActive(path) ? '600' : '400',
            fontSize: '0.9rem',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d1fae5';
            }
          }}
          onMouseLeave={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
          {label}
        </button>
      ))}

      {/* Tools section */}
      {toolsNavItems.some(canSee) && (
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#9ca3af',
        padding: '14px 14px 6px',
      }}>
        Tools
      </div>
      )}
      {toolsNavItems.filter(canSee).map(({ label, icon: Icon, path }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '4px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive(path) ? '#065f46' : 'transparent',
            color: isActive(path) ? 'white' : '#374151',
            fontWeight: isActive(path) ? '600' : '400',
            fontSize: '0.9rem',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d1fae5';
            }
          }}
          onMouseLeave={e => {
            if (!isActive(path)) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
          {label}
        </button>
      ))}

      {/* Webstore section */}
      {webstoreNavItems.some(canSee) && (
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#9ca3af',
        padding: '14px 14px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        <GlobeAltIcon style={{ width: '13px', height: '13px' }} />
        Webstore
      </div>
      )}
      {webstoreNavItems.filter(canSee).map(({ label, icon: Icon, path }) => (
        <button
          key={path}
          onClick={() => navigate(path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            marginBottom: '4px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive(path) ? '#065f46' : 'transparent',
            color: isActive(path) ? 'white' : '#374151',
            fontWeight: isActive(path) ? '600' : '400',
            fontSize: '0.9rem',
            textAlign: 'left',
            width: '100%',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive(path)) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d1fae5';
          }}
          onMouseLeave={e => {
            if (!isActive(path)) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          }}
        >
          <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
          {label}
        </button>
      ))}

      <div style={{ flex: 1 }} />

      <button
        onClick={handleLogout}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          borderRadius: '10px',
          border: '1px solid #d1fae5',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          color: '#6b7280',
          fontSize: '0.9rem',
          width: '100%',
          marginTop: '8px',
          transition: 'background-color 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#fee2e2';
          (e.currentTarget as HTMLButtonElement).style.color = '#dc2626';
          (e.currentTarget as HTMLButtonElement).style.borderColor = '#fecaca';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = '#6b7280';
          (e.currentTarget as HTMLButtonElement).style.borderColor = '#d1fae5';
        }}
      >
        <ArrowRightOnRectangleIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
        Logout
      </button>
    </nav>
  );
};

export default CompanySidebar;
